import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import Head from 'next/head';
import Button from '@components/atoms/button';
import { BodyRegular } from '@components/atoms/typography';
import { WEBSITE_NAME } from '@lib/constants';
import { datadogRumService } from '@lib/datadogRum';
import {
  AUTH_ERROR_REASONS,
  deriveReasonFromOAuthError,
  isAuthErrorReason,
  isExpectedAuthErrorReason
} from '@lib/auth0/auth-error-reasons';
import type { AuthErrorReason } from '@lib/auth0/auth-error-reasons';
import {
  buildAuthErrorRetryMarkerCookie,
  hasAuthErrorRetryMarker,
  shouldAutoRestartLogin
} from '@lib/auth0/auth-error-recovery';
import { isAppRelativeReturnTo } from '@lib/auth0/return-to';

const SUPPORT_URL = 'https://fantasyfootballhub.freshdesk.com/support/home';
const LOGIN_PATH = '/auth/login';
const HTTPS_PROTOCOL = 'https:';

type AuthErrorStateConfig = {
  badgeClassName: string;
  glyph: string;
  glyphClassName: string;
  headline: string;
  body: string;
  primaryLabel: string;
  hasSupportLine: boolean;
};

const UNKNOWN_STATE: AuthErrorStateConfig = {
  badgeClassName: 'bg-danger-50',
  glyph: '!',
  glyphClassName: 'text-[34px] text-danger-600',
  headline: 'Something went wrong signing you in',
  body: "Your login didn't complete. This is usually temporary, so trying again should sort it.",
  primaryLabel: 'Try logging in again',
  hasSupportLine: true
};

// Copy and styling per design handoff (auth callback error screen, option
// 1a): one layout, message and primary action vary by reason.
const AUTH_ERROR_STATES: Record<AuthErrorReason, AuthErrorStateConfig> = {
  [AUTH_ERROR_REASONS.verifyEmail]: {
    badgeClassName: 'bg-primary-50',
    glyph: '@',
    glyphClassName: 'text-[30px] text-primary-600',
    headline: 'Verify your email to continue',
    body: 'Check your inbox for a verification link, then log in again. It may take a minute to arrive.',
    primaryLabel: "I've verified, log in",
    hasSupportLine: false
  },
  [AUTH_ERROR_REASONS.loginCancelled]: {
    badgeClassName: 'bg-neutral-200',
    glyph: '×',
    glyphClassName: 'text-[34px] text-neutral-600',
    headline: 'Login cancelled',
    body: 'No problem, you can pick up where you left off.',
    primaryLabel: 'Try logging in again',
    hasSupportLine: false
  },
  [AUTH_ERROR_REASONS.sessionLost]: {
    badgeClassName: 'bg-neutral-200',
    glyph: '↻',
    glyphClassName: 'text-[34px] text-neutral-600',
    headline: 'Your login session expired',
    body: 'This usually happens after going back or refreshing during login. Logging in again should sort it.',
    primaryLabel: 'Try logging in again',
    hasSupportLine: true
  },
  [AUTH_ERROR_REASONS.stateMismatch]: UNKNOWN_STATE,
  [AUTH_ERROR_REASONS.codeExchangeFailed]: UNKNOWN_STATE,
  [AUTH_ERROR_REASONS.unknown]: UNKNOWN_STATE
};

const RESTARTING_STATE: AuthErrorStateConfig = {
  ...AUTH_ERROR_STATES[AUTH_ERROR_REASONS.sessionLost],
  headline: 'Restarting your login',
  body: 'Your login session expired, so we are taking you back to sign in.'
};

/**
 * Sets the one-shot marker and reports whether the browser kept it. A browser
 * that drops cookies cannot hold the fresh login transaction either, so the
 * failed write doubles as the cookies-present check.
 */
const persistRetryMarker = (): boolean => {
  document.cookie = buildAuthErrorRetryMarkerCookie(
    window.location.protocol === HTTPS_PROTOCOL
  );
  return hasAuthErrorRetryMarker(document.cookie);
};

const AuthErrorPage = () => {
  const router = useRouter();
  const {
    reason,
    error,
    error_description: errorDescription,
    returnTo
  } = router.query;

  // Prefer the sanitised reason the callback hook sets; fall back to deriving
  // from error/error_description params, which is how the Auth0 tenant's
  // error page lands users here without passing through /auth/callback.
  // Anything unrecognised is unknown.
  const resolvedReason: AuthErrorReason = isAuthErrorReason(reason)
    ? reason
    : deriveReasonFromOAuthError(
        typeof error === 'string' ? error : undefined,
        typeof errorDescription === 'string' ? errorDescription : undefined
      );

  const rawReturnTo = typeof returnTo === 'string' ? returnTo : undefined;
  const safeReturnTo = isAppRelativeReturnTo(rawReturnTo)
    ? rawReturnTo
    : undefined;
  // Always a plain full-page navigation into the login route: auth codes are
  // single-use, so recovery must re-initiate login, never retry the exchange.
  const loginHref = safeReturnTo
    ? `${LOGIN_PATH}?returnTo=${encodeURIComponent(safeReturnTo)}`
    : LOGIN_PATH;

  const [isRestarting, setIsRestarting] = useState(false);

  // Report once per page view so volume in RUM matches real arrivals
  // (FFH-1558 conventions; reason and recovery are facetable via @context).
  //
  // FFH-2860: only an unexpected failure is an ERROR. Cancelling a login is
  // the user's own choice and verifying an email is a normal onboarding step;
  // reporting either through addError made them indistinguishable from a
  // broken sign-in and put them in the browser error monitor's numerator.
  // Measured over the 7 days to 3 Sep 2026, 690 of 5,226 non-bot arrivals here
  // were one of those two. The server already draws this line (`isExpected`
  // in on-callback.ts decides console.warn versus console.error), so this
  // reuses it rather than inventing a second definition of what counts as a
  // fault. They are still reported, as actions, because they are funnel steps
  // someone will ask about.
  //
  // A recoverable lost-session arrival restarts the login once (FFH-2636):
  // the user usually still holds a live Auth0 session, so the fresh login
  // completes silently. The marker makes it one-shot so a repeat failure
  // renders this screen instead of looping.
  const hasHandledViewRef = useRef(false);
  useEffect(() => {
    if (!router.isReady || hasHandledViewRef.current) return;
    hasHandledViewRef.current = true;

    if (isExpectedAuthErrorReason(resolvedReason)) {
      datadogRumService.addAction('auth callback expected outcome', {
        reason: resolvedReason,
        failureMode: 'auth-callback'
      });
      return;
    }

    const willRestart =
      shouldAutoRestartLogin({
        reason: resolvedReason,
        hasRetryMarker: hasAuthErrorRetryMarker(document.cookie),
        canPersistCookies: navigator.cookieEnabled
      }) && persistRetryMarker();

    datadogRumService.addError(new Error('Auth callback failed'), {
      reason: resolvedReason,
      failureMode: 'auth-callback',
      recovery: willRestart ? 'auto-restart' : 'none'
    });

    if (willRestart) {
      setIsRestarting(true);
      window.location.assign(loginHref);
    }
  }, [router.isReady, resolvedReason, loginHref]);

  const state = isRestarting
    ? RESTARTING_STATE
    : AUTH_ERROR_STATES[resolvedReason];

  return (
    <>
      <Head>
        <title>{`Authentication error | ${WEBSITE_NAME}`}</title>
        <meta name="robots" content="noindex" />
      </Head>
      <div className="min-h-screen bg-neutral-50 flex flex-col">
        <div className="flex justify-center pt-8">
          <img
            src="/images/logos/ffh-text-logo-black.svg"
            alt="Fantasy Football Hub"
            className="h-7 w-auto"
          />
        </div>
        <main className="flex-1 flex flex-col items-center justify-center px-7 py-6">
          <div
            className={`w-[72px] h-[72px] rounded-full flex items-center justify-center ${state.badgeClassName}`}
          >
            <span
              className={`font-sans font-bold ${state.glyphClassName}`}
              aria-hidden="true"
            >
              {state.glyph}
            </span>
          </div>
          <h1 className="font-sans font-bold text-[26px] leading-[34px] text-neutral-800 text-center mt-6">
            {state.headline}
          </h1>
          <BodyRegular
            className="mt-3 text-center max-w-[300px]"
            textColor="text-neutral-700"
          >
            {state.body}
          </BodyRegular>
          <div className="flex flex-col gap-3 w-full max-w-[300px] mt-7">
            <Button variant="primary" href={loginHref} className="w-full">
              {state.primaryLabel}
            </Button>
            <Button variant="secondary" href="/" className="w-full">
              Return to home
            </Button>
          </div>
          {state.hasSupportLine && (
            <p className="font-open-sans text-[13px] text-neutral-500 mt-5">
              Still stuck?{' '}
              <a
                href={SUPPORT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600"
              >
                Contact support
              </a>
            </p>
          )}
        </main>
      </div>
    </>
  );
};

export default AuthErrorPage;
